import { useRef, useEffect, useState, useMemo } from "react";
import { ArrowRightIcon, ArrowLeftIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import useUIStore from "@/stores/useUIStore";
import { ListView, GridView } from "./TaskViews";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { moveTaskInDifferentList, moveTaskInSameList, parseDroppableId } from "@/helpers";
import { ExtendedMoveTaskParams, OptimisticUpdatedLists } from "../Task/types";
import { useMoveTask, useTasklists } from "@/hooks";
import { useTaskSortStore } from "@/stores/useTaskSortStore";
import { LockedAccountPlaceholder } from "@/components/LockedAccountPlaceholder";
import UpgradeDialog from "@/components/UpgradeDialog";

type MainContentProps = {
  accounts: Record<string, any>;
  showSidebar: boolean;
};

const MainContent = ({
  accounts,
  showSidebar,
}: MainContentProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const listOrder = useUIStore((state) => state.listOrder) || {};
  const checkboxes = useUIStore((state) => state.checkboxes);
  const layout = useUIStore((state) => state.layout);
  const moveTask = useMoveTask();
  const { getTasklistSortType } = useTaskSortStore();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  // Mandatory snap fights rbd's container auto-scroll, so lift it during drags
  const [isDragging, setIsDragging] = useState(false);

  const unlockedAccounts = useMemo(
    () => Object.fromEntries(Object.entries(accounts).filter(([, acc]) => !acc.locked)),
    [accounts]
  );

  const listQueries = useTasklists(unlockedAccounts);
  const setListOrder = useUIStore((state) => state.setListOrder);
  const setCheckbox = useUIStore((state) => state.setCheckbox);
  
  // Create a map of accountId -> query result for easy lookup
  // Extract accountId from query.data[0]?.accountId (added by getTasksListsForUser API)
  const accountListsMap = new Map<string, any[]>();
  listQueries.forEach((query) => {
    if (query.data && query.data.length > 0) {
      const accountId = query.data[0]?.accountId;
      if (accountId) {
        accountListsMap.set(accountId, query.data);
      }
    }
  });

  // Initialize listOrder/checkboxes and sync new lists created externally
  useEffect(() => {
    listQueries.forEach((query) => {
      if (!query.data || query.data.length === 0) return;
      
      const accountId = query.data[0]?.accountId;
      if (!accountId) return;
      
      const currentOrder = listOrder[accountId] || [];
      const currentCheckboxes = checkboxes[accountId];
      const allListIds = query.data.map((list: any) => list.id).filter(Boolean);
      const validListIds = new Set(allListIds);
      
      // Clean up stale IDs that no longer exist
      const cleanedOrder = currentOrder.filter((id: string) => validListIds.has(id));
      const newListIds = allListIds.filter((id: string) => !cleanedOrder.includes(id));
      
      if (currentOrder.length === 0) {
        setListOrder(accountId, allListIds);
      } else if (newListIds.length > 0 || cleanedOrder.length !== currentOrder.length) {
        // Update if there are new lists or stale IDs were removed
        setListOrder(accountId, [...cleanedOrder, ...newListIds]);
      }
      
      // Initialize checkboxes for any lists that don't have them set
      query.data.forEach((list: any) => {
        if (list.id && (!currentCheckboxes || typeof currentCheckboxes[list.id] !== 'boolean')) {
          setCheckbox(accountId, list.id, true);
        }
      });
    });
  }, [listQueries, listOrder, checkboxes, setListOrder, setCheckbox]);

  const scrollToEnd = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        left: contentRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  const scrollToStart = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const moveTaskInDifferentAccount = (_result: DropResult) => {
    // console.log("move task in different account", result)
    return null;
  }

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    const { destination, source } = result;
    if (!destination) return;

    // List-level reordering
    if (result.type === "list") {
      if (source.index === destination.index) return;
      const accountId = source.droppableId.replace("account-lists-", "");
      const currentOrder = listOrder[accountId];
      if (!currentOrder) return;

      // Build visible order to map indices back to the full order
      const visibleOrder = currentOrder.filter((id) => checkboxes[accountId]?.[id] ?? true);
      const movedId = visibleOrder[source.index];
      const targetId = visibleOrder[destination.index];

      const newOrder = [...currentOrder];
      const fromFull = newOrder.indexOf(movedId);
      const toFull = newOrder.indexOf(targetId);
      newOrder.splice(fromFull, 1);
      newOrder.splice(toFull, 0, movedId);

      setListOrder(accountId, newOrder);
      return;
    }

    // Task-level reordering
    const { accountId: sourceAccountId, listId: sourceListId } = parseDroppableId(source.droppableId);
    const { accountId: destinationAccountId, listId: destinationListId } = parseDroppableId(destination.droppableId);
    
    const sourceListSortType = getTasklistSortType(sourceAccountId, sourceListId);
    const destinationListSortType = getTasklistSortType(destinationAccountId, destinationListId);
    
    if (sourceListSortType !== "position" || destinationListSortType !== "position") {
      alert("Drag and drop is disabled for lists with non-default sorting. Enable it using the list menu on the top right corner of the list.");
      return;
    }
    
    const isDroppingInSameList = destinationListId === sourceListId;
    const isDroppingInDifferentAccount = sourceAccountId !== destinationAccountId;
    const isDroppingInDifferentList = destinationListId !== sourceListId;

    const cb = (moveTaskParams: ExtendedMoveTaskParams, optimisticUpdatedLists?: OptimisticUpdatedLists) => {
      moveTask.mutate({ ...moveTaskParams, ...optimisticUpdatedLists });
    }

    if (isDroppingInSameList) {
      return moveTaskInSameList(result, cb);
    } else if (isDroppingInDifferentAccount) {
      return moveTaskInDifferentAccount(result);
    } else if (isDroppingInDifferentList) {
      return moveTaskInDifferentList(result, cb);
    } else {
      console.log("invalid drag end")
    }
  }

  const getWidthClass = () => {
    return showSidebar ? "w-4/5" : "w-full";
  };

  return (
    <DragDropContext onDragStart={() => setIsDragging(true)} onDragEnd={onDragEnd}>
      <div
        className={`${getWidthClass()} max-md:w-full transition-width duration-300 bg-muted dark:bg-background relative overflow-hidden`}
      >
        <div className="absolute right-4 top-4 z-10 flex gap-2 pointer-events-none max-md:hidden">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full hover:bg-card hover:shadow pointer-events-auto"
            onClick={scrollToStart}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full hover:bg-card hover:shadow pointer-events-auto"
            onClick={scrollToEnd}
          >
            <ArrowRightIcon className="h-4 w-4" />
          </Button>
        </div>
        <div
          className={`${layout === "horizontal" ? "flex gap-4" : ""} overflow-x-auto p-4 scrollbar-thin scrollbar-thumb-muted-foreground scrollbar-track-background h-full ${layout === "grid" ? "max-md:overflow-x-hidden" : ""} ${isDragging ? "" : layout === "grid" ? "max-md:snap-y max-md:snap-mandatory" : "max-md:snap-x max-md:snap-mandatory"} max-md:scroll-px-4 max-md:scroll-pt-4`}
          ref={contentRef}
        >
        {Object.entries(accounts).map(([, account], i) => {
          if (account.locked) {
            return (
              // Wrapper carries the vertical snap alignment (mobile matrix view)
              // so mandatory snapping doesn't skip past locked accounts
              <div key={account.id} className="max-md:[scroll-snap-align:start_none]">
                <LockedAccountPlaceholder
                  account={account}
                  onUpgrade={() => setShowUpgradeDialog(true)}
                  variant={layout !== "grid" && layout !== "horizontal" ? "list" : "grid"}
                />
              </div>
            );
          }

          const accountLists = accountListsMap.get(account.id) || [];
          const listsMap = new Map(accountLists?.map((list: any) => [list.id, list]) as [string, any][]);
          
          // Use stored listOrder if available, otherwise derive from accountLists
          // This ensures lists render even before the effect initializes listOrder
          const storedOrder = listOrder[account.id];
          const order = (storedOrder && storedOrder.length > 0) 
            ? storedOrder 
            : accountLists.map((list: any) => list.id).filter(Boolean);

          return layout !== "grid" && layout !== "horizontal" ? (
            <ListView
              key={account.id}
              order={order}
              account={account}
              listsMap={listsMap}
              i={i}
              checkboxes={checkboxes}
            />
          ) : (
            <GridView
              key={account.id}
              order={order}
              account={account}
              listsMap={listsMap}
              i={i}
              checkboxes={checkboxes}
              independentScroll={layout === "grid"}
              dragActive={isDragging}
            />
          );
        })}
        {/* Mobile matrix view: room below the last account so it too can snap
            to the top of the screen like the first one */}
        {layout === "grid" && Object.keys(accounts).length > 1 && (
          <div aria-hidden className="hidden max-md:block max-md:h-[70dvh]" />
        )}
        </div>
      </div>

      <UpgradeDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog} source="main_content" />
    </DragDropContext>
  );
};

export default MainContent;
